import { useMemo, useState } from 'react';
import { Sparkles, FileText, AlertCircle, Wand2, Lock, Pencil, Presentation } from 'lucide-react';
import type { Influencer, Product } from '../../../shared/types';
import { createCampaign, startCampaign, errorText } from '../lib/api';
import ThumbWithPreview from './ThumbPreview';

type Mode = 'ai' | 'custom' | null;

export default function NewCampaignForm({
  influencers,
  products,
  onCreated,
  onEditInfluencer,
  onEditProduct,
  userId,
  admin = false,
}: {
  influencers: Influencer[];
  products: Product[];
  onCreated: (campaignId: string) => void;
  /** Opens the avatar editor — offered to the avatar's creator and admins. */
  onEditInfluencer?: (influencer: Influencer) => void;
  /** Opens the product editor — offered to the product's creator and admins. */
  onEditProduct?: (product: Product) => void;
  userId: string;
  admin?: boolean;
}) {
  const [mode, setMode] = useState<Mode>(null);
  const [influencerId, setInfluencerId] = useState('');
  const [productId, setProductId] = useState('');
  const [angle, setAngle] = useState('');
  const [customScript, setCustomScript] = useState('');
  const [aspectRatio, setAspectRatio] = useState<'9:16' | '16:9'>('9:16');
  const [customLocations, setCustomLocations] = useState('');
  const [isDemo, setIsDemo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A restricted influencer (non-empty allowed_users) is only offered to people
  // on the list. The server enforces this too; filtering here stops someone
  // creating a campaign the ACL would then hide from them.
  const availableInfluencers = useMemo(
    () =>
      influencers.filter(
        (i) => admin || !i.allowed_users?.length || i.allowed_users.includes(userId)
      ),
    [influencers, admin, userId]
  );

  // Brand → Product two-step picker, like v1: with several brands in the
  // product list, a flat dropdown makes the wrong pick too easy.
  const brands = useMemo(
    () => [...new Set(products.map((p) => p.brand).filter(Boolean))],
    [products]
  );
  const [brand, setBrand] = useState('');
  const brandProducts = useMemo(
    () => (brand ? products.filter((p) => p.brand === brand) : products),
    [products, brand]
  );

  const product = useMemo(() => products.find((p) => p.id === productId), [products, productId]);
  const influencer = useMemo(
    () => availableInfluencers.find((i) => i.id === influencerId),
    [availableInfluencers, influencerId]
  );

  const pickBrand = (next: string) => {
    setBrand(next);
    // Deselect a product that the new brand filter no longer shows.
    if (next && product && product.brand !== next) setProductId('');
  };

  const canSubmit =
    Boolean(influencerId && productId) && (mode !== 'custom' || customScript.trim().length > 0);

  // Survives a partial failure: if the campaign was created but starting it
  // failed, a retry starts the existing draft instead of creating a duplicate.
  const [createdId, setCreatedId] = useState<string | null>(null);

  const submit = async () => {
    if (!canSubmit || !mode) return;
    setBusy(true);
    setError(null);
    try {
      let campaignId = createdId;
      if (!campaignId) {
        const campaign = await createCampaign({
          title: product?.name ?? '',
          productId,
          influencerId,
          mode,
          angle: angle.trim(),
          customScript: mode === 'custom' ? customScript.trim() : '',
          aspectRatio,
          customLocations: customLocations
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
          isDemo: admin && isDemo,
        });
        campaignId = campaign.id;
        setCreatedId(campaign.id);
      }

      // The pipeline starts itself. In v1, approving a script did nothing until
      // someone opened n8n and clicked "Execute workflow".
      await startCampaign(campaignId, 'generate_script');
      onCreated(campaignId);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (!mode) {
    return (
      <div className="space-y-4">
        <div className="text-center space-y-1.5 py-2">
          <h2 className="font-serif text-xl font-medium">Start a new campaign</h2>
          <p className="text-xs text-natural-text/55">How would you like to write the script?</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <button
            onClick={() => setMode('custom')}
            className="zen-card p-6 text-left hover:border-natural-sage/50 transition-all cursor-pointer group"
          >
            <FileText size={22} className="text-natural-sage mb-3" />
            <h3 className="font-serif text-base font-medium mb-1 group-hover:text-natural-sage transition-colors">
              Paste my script
            </h3>
            <p className="text-xs text-natural-text/55 leading-relaxed">
              You provide the words. We split them into scenes, then generate images and video.
            </p>
          </button>

          <button
            onClick={() => setMode('ai')}
            className="zen-card p-6 text-left hover:border-natural-sage/50 transition-all cursor-pointer group"
          >
            <Sparkles size={22} className="text-natural-sage mb-3" />
            <h3 className="font-serif text-base font-medium mb-1 group-hover:text-natural-sage transition-colors">
              Write it for me
            </h3>
            <p className="text-xs text-natural-text/55 leading-relaxed">
              We draft a script from the product and your chosen angle, then you review it.
            </p>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="zen-card p-6 space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-serif text-lg font-medium">
          {mode === 'custom' ? 'New campaign — your script' : 'New campaign — AI script'}
        </h2>
        <button
          onClick={() => setMode(null)}
          className="text-xs text-natural-text/50 hover:text-natural-sage cursor-pointer"
        >
          Change
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="zen-label">Influencer</label>
          <select
            value={influencerId}
            onChange={(e) => setInfluencerId(e.target.value)}
            className="zen-input"
          >
            <option value="">Choose…</option>
            {availableInfluencers.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
                {i.allowed_users?.length ? ' 🔒' : ''}
              </option>
            ))}
          </select>
          {availableInfluencers.length === 0 && (
            <p className="text-[11px] text-amber-700 mt-1.5">
              No influencers exist yet. An admin can add them in the Admin tab.
            </p>
          )}

          {influencer && (
            <div className="mt-3 p-3 bg-natural-bg rounded-xl border border-natural-border/60 flex items-center gap-3">
              <ThumbWithPreview
                url={influencer.avatar_url || influencer.base_image_url}
                alt={influencer.name}
              />
              <div className="min-w-0 space-y-1 flex-1">
                <p className="text-sm font-semibold leading-none truncate">{influencer.name}</p>
                {influencer.allowed_users?.length ? (
                  <span className="zen-pill bg-purple-100 text-purple-700">
                    <Lock size={10} />
                    Private access
                  </span>
                ) : (
                  <p className="text-[11px] text-natural-text/45">Available to everyone</p>
                )}
              </div>
              {onEditInfluencer && (admin || influencer.created_by === userId) && (
                <button
                  onClick={() => onEditInfluencer(influencer)}
                  className="p-1.5 rounded-lg text-natural-text/40 hover:text-natural-sage hover:bg-white transition-colors cursor-pointer shrink-0"
                  title="Edit this avatar (its details, locations and privacy)"
                >
                  <Pencil size={13} />
                </button>
              )}
            </div>
          )}
        </div>

        <div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="zen-label">Brand</label>
              <select
                value={brand}
                onChange={(e) => pickBrand(e.target.value)}
                className="zen-input"
              >
                <option value="">All brands</option>
                {brands.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="zen-label">Product</label>
              <select
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                className="zen-input"
              >
                <option value="">Choose…</option>
                {brandProducts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {brand ? p.name : p.brand ? `${p.brand} — ${p.name}` : p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {product && (
            <div className="mt-3 p-3 bg-natural-bg rounded-xl border border-natural-border/60 flex items-center gap-3">
              <ThumbWithPreview url={product.image_url} alt={product.name} />
              <div className="min-w-0 flex-1 text-[11px] text-natural-text/65 leading-relaxed">
                <span className="font-semibold text-natural-text text-xs">{product.name}</span>
                {product.benefits
                  ? ` — ${product.benefits.slice(0, 180)}${product.benefits.length > 180 ? '…' : ''}`
                  : ''}
                {/* Product guidelines never reached the script in v1: it read
                    `Products!A:G` while the column sits at H, so this was always empty. */}
                {product.guidelines && (
                  <p className="mt-1 text-natural-sage">
                    Product guidelines will be included in the script prompt.
                  </p>
                )}
              </div>
              {onEditProduct && (admin || product.created_by === userId) && (
                <button
                  onClick={() => onEditProduct(product)}
                  className="p-1.5 rounded-lg text-natural-text/40 hover:text-natural-sage hover:bg-white transition-colors cursor-pointer shrink-0"
                  title="Edit this product (its details, benefits and photo)"
                >
                  <Pencil size={13} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="zen-label">Format</label>
          <div className="flex gap-1 p-1 bg-stone-100/80 rounded-xl">
            {(['9:16', '16:9'] as const).map((ratio) => (
              <button
                key={ratio}
                onClick={() => setAspectRatio(ratio)}
                className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                  aspectRatio === ratio ? 'bg-white shadow-xs' : 'text-natural-text/60'
                }`}
              >
                {ratio === '9:16' ? 'Vertical 9:16' : 'Wide 16:9'}
              </button>
            ))}
          </div>
        </div>

        {mode === 'ai' && (
          <div>
            <label className="zen-label">Angle (optional)</label>
            <input
              value={angle}
              onChange={(e) => setAngle(e.target.value)}
              placeholder="e.g. focus on morning energy"
              className="zen-input"
            />
          </div>
        )}
      </div>

      {mode === 'custom' && (
        <div>
          <label className="zen-label">Your script</label>
          <textarea
            value={customScript}
            onChange={(e) => setCustomScript(e.target.value)}
            rows={10}
            placeholder="Paste the full script. It will be split into scenes automatically."
            className="zen-input font-mono text-xs leading-relaxed resize-y"
          />
          <p className="text-[11px] text-natural-text/45 mt-1.5">
            {customScript.trim() ? `${customScript.trim().split(/\s+/).length} words` : ' '}
          </p>
        </div>
      )}

      <details className="group">
        <summary className="text-xs text-natural-text/55 cursor-pointer hover:text-natural-sage select-none">
          Custom locations (optional)
        </summary>
        <div className="mt-3">
          <input
            value={customLocations}
            onChange={(e) => setCustomLocations(e.target.value)}
            placeholder="Comma-separated Drive links"
            className="zen-input text-xs"
          />
          <p className="text-[11px] text-natural-text/45 mt-1.5">
            Referenced in the script as <code className="font-mono">cust_loc_01</code>,{' '}
            <code className="font-mono">cust_loc_02</code>, and so on.
            {influencer && ` Otherwise ${influencer.name}'s own locations are used.`}
          </p>
        </div>
      </details>

      {admin && (
        <label
          className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
            isDemo ? 'border-purple-300 bg-purple-50' : 'border-natural-border/60 bg-natural-bg'
          }`}
        >
          <input
            type="checkbox"
            checked={isDemo}
            onChange={(e) => setIsDemo(e.target.checked)}
            className="mt-0.5 accent-purple-600"
          />
          <span className="min-w-0">
            <span className="flex items-center gap-1.5 text-xs font-semibold">
              <Presentation size={13} className="text-purple-600" />
              Demo campaign (presentation only)
            </span>
            <span className="block text-[11px] text-natural-text/55 leading-relaxed mt-0.5">
              Visible to admins only and left out of Statistics. Every stage plays out with the
              pre-made script, images and clips from Admin → Demo assets — nothing is generated
              and nothing is billed.
            </span>
          </span>
        </label>
      )}

      {error && (
        <div className="p-3 bg-red-50 text-red-600 border border-red-200 rounded-xl text-xs flex items-start gap-2">
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <button onClick={submit} disabled={!canSubmit || busy} className="zen-btn-primary w-full">
        <Wand2 size={15} />
        {busy ? 'Creating…' : mode === 'custom' ? 'Create and split into scenes' : 'Create and write the script'}
      </button>
    </div>
  );
}
